import React, {useState, useEffect} from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    Chip,
    LinearProgress,
} from '@mui/material';
import {
    RestartAlt as RestartIcon,
    History as HistoryIcon,
    School as SchoolIcon,
} from '@mui/icons-material';

// Import the new DetailedQuestionResult component
import DetailedQuestionResult from './DetailedQuestionResult';
import ChatInterface from './ChatInterface';
import Quantino from '../../../components/Quantino/Quantino.jsx';
import '../../../components/Quantino/Quantino.css';

// Helper function to format score nicely
const formatScore = (score) => {
    if (score % 1 === 0) {
        // If it's a whole number, return as integer
        return score.toString();
    } else if (score % 0.5 === 0) {
        // If it's a half number (like 1.5), show one decimal
        return score.toFixed(1);
    } else {
        // For other decimals, round to 2 decimal places
        return (Math.round(score * 100) / 100).toString();
    }
};

const SubmissionSummary = ({
                               submission,
                               answers,
                               formData,
                               onRestart,
                               onViewHistory,
                               score,
                               maxScore,
                               accessToken, // Add accessToken prop
                               isGamificationModalOpen = false // Add this prop
                           }) => {
    // State for managing chat interface
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [quantino, setQuantino] = useState({
        mood: 'neutral',
        message: '',
        isVisible: false,
        isBubbleVisible: false,
    });

    if (!submission || !answers || !formData) {
        return (
            <Container maxWidth="lg" sx={{mt: 4}}>
                <Typography>Loading summary...</Typography>
            </Container>
        );
    }

    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const isPassed = percentage >= 60;

    useEffect(() => {
        // Don't show Quantino if the gamification modal is open
        if (isGamificationModalOpen) {
            return;
        }

        const timer = setTimeout(() => {
            // Define mood arrays with more variety
            const excellentMoods = ['feiernd', 'stolz_daumen_hoch', 'überrascht', 'freundlich'];
            const goodMoods = ['motivierend', 'freundlich', 'zwinkernd', 'stolz_daumen_hoch'];
            const okayMoods = ['nachdenklich', 'motivierend', 'neutral', 'freundlich'];
            const poorMoods = ['streng', 'traurig', 'nachdenklich', 'motivierend'];

            // Select mood and message based on performance
            let selectedMood, selectedMessage;
            
            if (percentage >= 90) {
                selectedMood = excellentMoods[Math.floor(Math.random() * excellentMoods.length)];
                const messages = [
                    `Outstanding! ${percentage}% - You're absolutely crushing it!`,
                    `Incredible work! ${percentage}% is truly exceptional!`,
                    `Wow! ${percentage}% - You're a real quiz champion!`,
                    `Amazing! ${percentage}% - Your dedication is paying off!`
                ];
                selectedMessage = messages[Math.floor(Math.random() * messages.length)];
            } else if (percentage >= 80) {
                selectedMood = goodMoods[Math.floor(Math.random() * goodMoods.length)];
                const messages = [
                    `Great job! ${percentage}% is excellent work!`,
                    `Well done! ${percentage}% shows real understanding!`,
                    `Nice! ${percentage}% - You're doing really well!`,
                    `Fantastic! ${percentage}% is a solid achievement!`
                ];
                selectedMessage = messages[Math.floor(Math.random() * messages.length)];
            } else if (percentage >= 60) {
                selectedMood = okayMoods[Math.floor(Math.random() * okayMoods.length)];
                const messages = [
                    `Good effort! ${percentage}% means you're on the right track!`,
                    `Not bad! ${percentage}% - keep building on this foundation!`,
                    `You passed! ${percentage}% is a solid start - let's aim higher next time!`,
                    `Well done passing! ${percentage}% shows you're learning!`
                ];
                selectedMessage = messages[Math.floor(Math.random() * messages.length)];
            } else {
                selectedMood = poorMoods[Math.floor(Math.random() * poorMoods.length)];
                const messages = [
                    `${percentage}% - Don't give up! Every mistake is a learning opportunity!`,
                    `${percentage}% this time, but I believe in you! Try again when you're ready!`,
                    `${percentage}% - You're still learning, and that's perfectly okay! Keep going!`,
                    `${percentage}% - This is just the beginning! Every expert was once a beginner!`
                ];
                selectedMessage = messages[Math.floor(Math.random() * messages.length)];
            }

            setQuantino({
                mood: selectedMood,
                message: selectedMessage,
                isVisible: true,
                isBubbleVisible: true,
            });

            // Auto-hide bubble after 8 seconds
            const hideTimer = setTimeout(() => {
                setQuantino(prev => ({...prev, isBubbleVisible: false}));
                
                // Keep Quantino visible but smaller after 2 more seconds
                const minimizeTimer = setTimeout(() => {
                    setQuantino(prev => ({...prev, isVisible: true})); // Keep visible, just smaller
                }, 2000);

                return () => clearTimeout(minimizeTimer);
            }, 8000);

            return () => clearTimeout(hideTimer);
        }, 500); // Delay to allow the main animation to settle

        return () => clearTimeout(timer);
    }, [isPassed, percentage, isGamificationModalOpen]);

    const answersBySection = {};
    Object.values(answers).forEach(answer => {
        const question = answer.question || formData.questions?.find(q => q.id === answer.question_id);
        if (question) {
            const sectionId = question.sectionId || question.section_id;
            if (!answersBySection[sectionId]) {
                answersBySection[sectionId] = [];
            }
            answersBySection[sectionId].push({...answer, question});
        }
    });

    // Handle opening chat for a specific question/answer
    const handleOpenChat = (question, answer) => {
        setSelectedQuestion(question);
        setSelectedAnswer(answer);
        setChatOpen(true);
    };

    const handleCloseChat = () => {
        setChatOpen(false);
        setSelectedQuestion(null);
        setSelectedAnswer(null);
    };

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 8}}>
            <Quantino
                mood={quantino.mood}
                message={quantino.message}
                isVisible={quantino.isVisible}
                isBubbleVisible={quantino.isBubbleVisible}
            />
            <Paper sx={{p: 4, mb: 4}}>
                <Box sx={{textAlign: 'center', mb: 3}}>
                    <SchoolIcon sx={{fontSize: 48, color: isPassed ? 'success.main' : 'warning.main', mb: 2}}/>
                    <Typography variant="h3" gutterBottom>
                        {isPassed ? 'Congratulations!' : 'Keep Learning!'}
                    </Typography>
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                        You scored {formatScore(score)} out of {formatScore(maxScore)} points
                    </Typography>

                    <Box sx={{width: '100%', maxWidth: 400, mx: 'auto', mt: 3}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
                            <Typography variant="body2">Progress</Typography>
                            <Typography variant="body2">{percentage}%</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: isPassed ? 'success.main' : 'warning.main'
                                }
                            }}
                        />
                    </Box>

                    <Chip
                        label={isPassed ? 'PASSED' : 'NEEDS IMPROVEMENT'}
                        color={isPassed ? 'success' : 'warning'}
                        size="large"
                        sx={{mt: 2, fontSize: '1rem', fontWeight: 'bold'}}
                    />
                </Box>

                <Grid container spacing={2} sx={{mt: 2}}>
                    {onRestart && (
                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<RestartIcon/>}
                                onClick={onRestart}
                                size="large"
                            >
                                Try Again
                            </Button>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={onRestart ? 6 : 12}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<HistoryIcon/>}
                            onClick={onViewHistory}
                            size="large"
                        >
                            {onRestart ? 'View History' : 'Back to History'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h4" gutterBottom sx={{mb: 3}}>
                Detailed Results
            </Typography>

            {formData.sections?.map(section => {
                const sectionAnswers = answersBySection[section.id] || [];
                const correctCount = sectionAnswers.filter(a => a.is_correct).length;
                const totalCount = sectionAnswers.length;
                const sectionPercentage = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;

                return (
                    <Paper key={section.id} sx={{mb: 3}}>
                        <Box sx={{p: 3, borderBottom: '1px solid', borderColor: 'divider'}}>
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <Typography variant="h5">{section.title}</Typography>
                                <Chip
                                    label={`${correctCount}/${totalCount} correct (${sectionPercentage}%)`}
                                    color={sectionPercentage >= 60 ? 'success' : 'error'}
                                    variant="outlined"
                                />
                            </Box>
                            {section.description && (
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                    {section.description}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            {sectionAnswers.map((answer) => (
                                <DetailedQuestionResult
                                    key={answer.question_id}
                                    answer={answer}
                                    question={answer.question}
                                    onChatWithTutor={handleOpenChat}
                                    accessToken={accessToken}
                                />
                            ))}
                        </Box>
                    </Paper>
                );
            })}

            {/* Chat Interface */}
            <ChatInterface
                open={chatOpen}
                onClose={handleCloseChat}
                question={selectedQuestion}
                answer={selectedAnswer}
                accessToken={accessToken}
            />
        </Container>
    );
};

export default SubmissionSummary;